import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiTrendingUp, FiArrowRight, FiArrowLeft, FiMail, FiInstagram, FiFacebook, FiYoutube} = FiIcons;

const Step3 = ({ formData, updateFormData, onNext, onPrev }) => {
  const marketingChannels = [
    'Email List', 'Podcasting', 'Instagram', 'Blog Post', 
    'Facebook', 'Speaking', 'YouTube', 'Partners', 'TikTok', 'Other'
  ];

  const adPlatforms = ['Facebook', 'Amazon', 'Instagram', 'Pinterest', 'BookBub'];

  const handleChannelToggle = (channel) => {
    const current = formData.chosenChannels || [];
    if (current.includes(channel)) {
      updateFormData({ chosenChannels: current.filter(c => c !== channel) });
    } else if (current.length < 3) {
      updateFormData({ chosenChannels: [...current, channel] });
    }
  };

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const canProceed = 
    formData.contentStyle && 
    formData.authorStrengths && 
    formData.chosenChannels?.length > 0 && 
    formData.chosenAdPlatform;

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-orange-500 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <SafeIcon icon={FiTrendingUp} className="text-2xl text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Step 3: Choose Your Marketing Strategy
          </h2>
          <p className="text-gray-600">
            Select 1-3 marketing methods that best match your strengths, audience, and bandwidth.
          </p>
        </div>

        <div className="space-y-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <label className="block text-lg font-semibold text-gray-700 mb-3">
              What kind of content feels natural for you to create?
            </label>
            <textarea
              value={formData.contentStyle}
              onChange={(e) => handleInputChange('contentStyle', e.target.value)}
              placeholder="Ex: short emails, devotional posts, video chats, teaching content..."
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none h-20 text-gray-700"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <label className="block text-lg font-semibold text-gray-700 mb-3">
              What are your author strengths/gifts?
            </label>
            <textarea
              value={formData.authorStrengths}
              onChange={(e) => handleInputChange('authorStrengths', e.target.value)}
              placeholder="Ex: storytelling, encouragement, teaching, connecting with people..."
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none h-20 text-gray-700"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <label className="block text-lg font-semibold text-gray-700 mb-4">
              Select 1-3 Marketing Channels (Pick what fits you best!)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {marketingChannels.map((channel, index) => (
                <motion.button
                  key={channel}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleChannelToggle(channel)}
                  className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    formData.chosenChannels?.includes(channel)
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  } ${formData.chosenChannels?.length >= 3 && !formData.chosenChannels?.includes(channel) 
                      ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={formData.chosenChannels?.length >= 3 && !formData.chosenChannels?.includes(channel)}
                >
                  {channel}
                </motion.button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Selected: {formData.chosenChannels?.length || 0}/3
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <label className="block text-lg font-semibold text-gray-700 mb-4">
              Choose 1 Ad Platform to Focus On
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {adPlatforms.map((platform) => (
                <motion.button
                  key={platform}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleInputChange('chosenAdPlatform', platform)}
                  className={`p-4 rounded-xl border-2 font-medium transition-all ${
                    formData.chosenAdPlatform === platform
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  {platform}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="flex justify-between mt-8">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onPrev}
            className="bg-gray-500 text-white px-6 py-3 rounded-xl font-semibold flex items-center hover:bg-gray-600 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back
          </motion.button>

          <motion.button
            whileHover={{ scale: canProceed ? 1.05 : 1 }}
            whileTap={{ scale: canProceed ? 0.95 : 1 }}
            onClick={onNext}
            disabled={!canProceed}
            className={`px-6 py-3 rounded-xl font-semibold flex items-center transition-all ${
              canProceed
                ? 'bg-gradient-to-r from-blue-600 to-orange-500 text-white hover:shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continue
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default Step3;